import React, { useState, useEffect } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import './HomepageCard.scss';
import { BASE_BUY_URL, BASE_CHART_URL } from '../../../config/siteContent';

const REDDICKS_RESOURCE_ADDRESS = 'resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp';
const ASTROLESCENT_API_URL = 'https://api.astrolescent.com/partner/reddicks/prices';
const JUSTLOCK_API_URL = `https://api.justlock.io/resource/${REDDICKS_RESOURCE_ADDRESS}`;
const OCISWAP_API_URL = `https://api.ociswap.com/tokens/${REDDICKS_RESOURCE_ADDRESS}`;

const HomepageCard = () => {
  const [tokenData, setTokenData] = useState(null);
  const [lockData, setLockData] = useState(null);
  const [ociswapData, setOciswapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const priceResponse = await fetch(ASTROLESCENT_API_URL);
        if (!priceResponse.ok) throw new Error('Failed to fetch price data from Astrolescent');
        const priceData = await priceResponse.json();
        const reddicksPriceData = priceData[REDDICKS_RESOURCE_ADDRESS];
        if (!reddicksPriceData) throw new Error('REDDICKS token data not found');
        setTokenData(reddicksPriceData);

        const lockResponse = await fetch(JUSTLOCK_API_URL);
        if (!lockResponse.ok) { setLockData(null); }
        else { setLockData(await lockResponse.json()); }

        const ociswapResponse = await fetch(OCISWAP_API_URL);
        if (!ociswapResponse.ok) { setOciswapData(null); }
        else { setOciswapData(await ociswapResponse.json()); }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching token data:", err);
        setError(err.message || 'Failed to load token data.');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Fixed-delay mount animation (synced with TokenomicsCard)
  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 800);
    return () => clearTimeout(t);
  }, []);

  const formatNumber = (num, decimals = 2) => {
    if (typeof num !== 'number') return 'N/A';
    return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const formatLargeNumber = (num) => {
    if (typeof num !== 'number' || isNaN(num)) return 'N/A';
    if (num >= 1e9) return '$' + (num / 1e9).toFixed(2) + 'B';
    if (num >= 1e6) return '$' + (num / 1e6).toFixed(2) + 'M';
    if (num >= 1e3) return '$' + (num / 1e3).toFixed(1) + 'K';
    return '$' + num.toFixed(0);
  };

  const formatPrice = (price) => {
    if (typeof price !== 'number') return 'N/A';
    if (price < 0.0001 && price > 0) return price.toExponential(2);
    if (price < 1) return price.toFixed(6);
    return formatNumber(price, 2);
  };

  const formatPercentage = (diff) => {
    if (typeof diff !== 'number') return { value: 'N/A', className: '', arrow: '' };
    const value = (diff * 100).toFixed(2);
    const className = diff > 0 ? 'positive' : diff < 0 ? 'negative' : 'neutral';
    const arrow = diff > 0 ? '+' : diff < 0 ? '-' : '';
    return { value: `${arrow} ${Math.abs(value)}%`, className };
  };

  const totalSupply = tokenData?.totalSupply ? parseFloat(tokenData.totalSupply) : 0;
  const lockedAmount = lockData?.lock_summary?.amount ? parseFloat(lockData.lock_summary.amount) : 0;
  const lockedPercentage = totalSupply > 0 && lockedAmount > 0 ? ((lockedAmount / totalSupply) * 100).toFixed(1) : 0;

  const priceUSD = tokenData?.tokenPriceUSD;
  const priceXRD = tokenData?.tokenPriceXRD;
  const diff7DaysUSD = formatPercentage(tokenData?.diff7DaysUSD);
  const diff7DaysXRD = formatPercentage(tokenData?.diff7Days);

  const marketCapUSDString = ociswapData?.market_cap?.circulating?.usd?.now;
  const marketCapUSD = marketCapUSDString ? parseFloat(marketCapUSDString) : null;

  return (
    <div className={`homepage-card ${isVisible ? 'visible' : ''} ${loading ? 'loading' : ''} ${error ? 'error' : ''}`}>
      {/* Decorative elements */}
      <div className="hpc-glow"></div>
      <div className="hpc-accent-line"></div>

      <div className="hpc-inner">
        {loading && <div className="hpc-loading"><div className="hpc-spinner"></div></div>}
        {error && <div className="hpc-error">Error: {error}</div>}
        {!loading && !error && tokenData && lockData && (
          <>
            {/* === Header: Icon + Name + Live dot === */}
            <div className="hpc-header">
              <img src={tokenData.iconUrl} alt={`${tokenData.name} logo`} className="hpc-token-icon" />
              <div className="hpc-header-text">
                <span className="hpc-symbol">{tokenData.symbol}</span>
                <span className="hpc-label">Live Stats</span>
              </div>
              <div className="hpc-live-dot"></div>
            </div>

            {/* === Hero Price === */}
            <div className="hpc-price-hero">
              <div className="hpc-price-main">
                <span className="hpc-currency">$</span>
                <span className="hpc-price-value">{formatPrice(priceUSD)}</span>
              </div>
              <div className="hpc-price-badges">
                <span className={`hpc-badge ${diff7DaysUSD.className}`}>
                  USD {diff7DaysUSD.value}
                </span>
                <span className={`hpc-badge ${diff7DaysXRD.className}`}>
                  XRD {diff7DaysXRD.value}
                </span>
              </div>
            </div>

            {/* === XRD Price Row === */}
            <div className="hpc-xrd-row">
              <span className="hpc-xrd-label">XRD Price</span>
              <span className="hpc-xrd-value">{formatPrice(priceXRD)} XRD</span>
            </div>

            {/* === Stat Grid === */}
            <div className="hpc-stat-grid">
              <div className="hpc-stat">
                <span className="hpc-stat__label">Market Cap</span>
                <span className="hpc-stat__value">{formatLargeNumber(marketCapUSD)}</span>
              </div>
              <div className="hpc-stat">
                <span className="hpc-stat__label">Supply</span>
                <span className="hpc-stat__value">{formatNumber(totalSupply, 0)}</span>
              </div>
              <div className="hpc-stat hpc-stat--full">
                <span className="hpc-stat__label">Locked</span>
                <span className="hpc-stat__value">
                  {lockData ? `${formatNumber(lockedAmount, 0)}` : 'N/A'}
                </span>
                {lockData && <span className="hpc-stat__pct">{lockedPercentage}%</span>}
              </div>
            </div>

            {/* === Footer === */}
            <div className="hpc-footer">
              <a
                href={BASE_BUY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hpc-trade-btn"
              >
                Buy on Base <FaExternalLinkAlt />
              </a>
              <a
                href={BASE_CHART_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="hpc-trade-btn hpc-trade-btn--secondary"
              >
                View Chart <FaExternalLinkAlt />
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HomepageCard;
